import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";

// Register custom font and size with inline styles
const Size = Quill.import("attributors/style/size") as any;
Size.whitelist = [
  "8px",
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "24px",
  "32px",
  "48px",
];
Quill.register(Size, true);

// Use inline style for font family, override default font attributor
const FontStyle = Quill.import("attributors/style/font") as any;
FontStyle.whitelist = [
  "sans-serif",
  "serif",
  "monospace",
  "roboto",
  "georgia",
  "tahoma",
  "verdana",
  "arial",
  "comic-sans",
];
Quill.register("formats/font", FontStyle, true); // Override default font attributor

// Register background and color attributors
const BackgroundStyle = Quill.import("attributors/style/background") as any;
Quill.register(BackgroundStyle, true);

const ColorStyle = Quill.import("attributors/style/color") as any;
Quill.register(ColorStyle, true);

export const quillModules = {
  toolbar: [
    [{ font: FontStyle.whitelist }, { size: Size.whitelist }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ header: [1, 2, 3, false] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false, // Prevent Quill from interpreting HTML structure
  },
};

export const quillFormats = [
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "script",
  "header",
  "list",
  "bullet",
  "indent",
  "align",
  "link",
  "image",
];

interface QuillWrapperProps {
  value: string;
  onChange: (content: string, delta: any, source: any, editor: any) => void;
  onBlur: () => void;
  modules?: any;
  formats?: string[];
  className?: string;
}

const QuillWrapper: React.FC<QuillWrapperProps> = ({
  value,
  onChange,
  onBlur,
  modules = quillModules,
  formats = quillFormats,
  className = "",
}) => {
  const quillRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<any>(null);
  const handlerRef = useRef<
    ((delta: any, oldDelta: any, source: string) => void) | null
  >(null);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    if (quillRef.current && !quillInstanceRef.current) {
      const quill = new Quill(quillRef.current, {
        theme: "snow",
        modules,
        formats,
      });

      quillInstanceRef.current = quill;

      // Apply default font and size to unformatted text only during user interaction
      quill.on("editor-change", (name: string, ...args: any[]) => {
        if (name === "selection-change") {
          const range = quill.getSelection();
          if (range) {
            const formats = quill.getFormat(range);
            if (!formats.font) {
              quill.formatText(range.index, range.length, "font", "arial");
            }
            if (!formats.size) {
              quill.formatText(range.index, range.length, "size", "12px");
            }
          }
        }
      });

      const handleChange = (delta: any, oldDelta: any, source: string) => {
        if (source === "user" && !isUpdatingRef.current) {
          const editorHTML = quill.root.innerHTML;
          console.log("Raw Quill content on change:", editorHTML);
          console.log("Quill delta on change:", quill.getContents());
          onChange(editorHTML, delta, source, quill);
        }
      };

      const handleBlur = () => {
        onBlur();
      };

      handlerRef.current = handleChange;
      quill.on("text-change", handleChange);
      quill.root.addEventListener("blur", handleBlur);

      (quill as any).__blurHandler = handleBlur;

      return () => {
        const quill = quillInstanceRef.current;
        if (quill && handlerRef.current) {
          quill.off("text-change", handlerRef.current);
          if ((quill as any).__blurHandler) {
            quill.root.removeEventListener(
              "blur",
              (quill as any).__blurHandler
            );
          }
        }
      };
    }
  }, [onChange, onBlur, modules, formats]);

  useEffect(() => {
    const quill = quillInstanceRef.current;
    if (quill && value !== undefined) {
      const currentContent = quill.root.innerHTML;
      const normalizedValue = value || "";
      const normalizedCurrent = currentContent || "";

      const isDifferent = normalizedCurrent !== normalizedValue;

      if (isDifferent && !isUpdatingRef.current) {
        isUpdatingRef.current = true;

        // Temporarily disable text-change handler to prevent Delta conversion
        if (handlerRef.current) {
          quill.off("text-change", handlerRef.current);
        }

        // Store the current selection
        const currentSelection = quill.getSelection();
        const cursorPosition = currentSelection
          ? currentSelection.index
          : quill.getLength() - 1;

        // Set raw HTML directly
        quill.root.innerHTML = normalizedValue;
        console.log("Set raw Quill content:", normalizedValue);

        // Check if content has any formatting
        const delta = quill.getContents();
        let hasFormatting = false;
        if (delta.ops) {
          delta.ops.forEach(
            (op: {
              attributes?: {
                font?: string;
                size?: string;
                bold?: boolean;
                italic?: boolean;
                strike?: boolean;
                underline?: boolean;
                color?: string;
                background?: string;
              };
            }) => {
              if (
                op.attributes &&
                (op.attributes.font ||
                  op.attributes.size ||
                  op.attributes.bold ||
                  op.attributes.italic ||
                  op.attributes.strike ||
                  op.attributes.underline ||
                  op.attributes.color ||
                  op.attributes.background)
              ) {
                hasFormatting = true;
              }
            }
          );
        }

        // Apply default formatting only to unformatted content
        if (!hasFormatting && normalizedValue) {
          quill.formatText(0, quill.getLength(), {
            font: "arial",
            size: "12px",
          });
          console.log("Applied default formatting: Arial, 12px");
          const updatedHTML = quill.root.innerHTML;
          console.log(
            "Updated raw Quill content after default formatting:",
            updatedHTML
          );
          onChange(updatedHTML, quill.getContents(), "api", quill);
        }

        // Restore the cursor position
        setTimeout(() => {
          const newLength = quill.getLength();
          const safePosition = Math.min(cursorPosition, newLength - 1);
          quill.setSelection(safePosition, 0);
        }, 0);

        // Re-enable text-change handler
        if (handlerRef.current) {
          quill.on("text-change", handlerRef.current);
        }

        isUpdatingRef.current = false;
      }
    }
  }, [value, onChange]);

  return <div ref={quillRef} className={className} />;
};

// Helper component to display Quill content with proper formatting
export const QuillContentDisplay: React.FC<{
  content: string;
  className?: string;
}> = ({ content, className = "" }) => {
  return (
    <div className={`quill-content-display ${className}`}>
      <div className="ql-container ql-snow">
        <div
          className="ql-editor"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  );
};

export default QuillWrapper;
